import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { ArrowUpRight, Bot, Check, Database, FlaskConical, LockKeyhole, Microscope, Pause, Pipette, Play, RotateCcw, Workflow } from "lucide-react";
import "./DiscoveryDeployment.css";
import { createDeploymentScene, DEPLOYMENT_DURATION, DEPLOYMENT_STOPS, deploymentStage, smoothProgress } from "./deploymentMotion";

const deploymentStages = [
  { label: "Deploy", caption: "Cornucopia runs inside your company’s research environment." },
  { label: "Connect", caption: "Your AI team works with your data and lab systems." },
  { label: "Experiment", caption: "Your scientist reviews the plan before the lab runs it." },
  { label: "Advance", caption: "Each result helps the team decide what to test next." },
];

const partnershipStages = [
  { label: "Deploy", caption: "We deploy the AI harness inside the partner’s research environment." },
  { label: "Connect", caption: "Specialist agents work with the partner’s research history and lab systems." },
  { label: "Experiment", caption: "Scientists review the experiment before the lab runs it." },
  { label: "Advance", caption: "Our goal is to turn promising research into co-developed drug candidates." },
];

function DataNode({ cornucopia = false, partnership = false }) {
  const researchSystems = cornucopia && partnership;
  return (
    <div className={`deployment-database deployment-reveal deployment-connect ${cornucopia ? "deployment-data-ours" : "deployment-data-theirs"}`}>
      {researchSystems ? <Workflow aria-hidden="true" strokeWidth={1.15} /> : <Database aria-hidden="true" strokeWidth={1.15} />}
      <strong>{researchSystems ? "Research tools & controls" : cornucopia ? "Cornucopia data" : partnership ? "Partner biology & history" : "Your proprietary data"}</strong>
      {!cornucopia && <span><LockKeyhole size={10} /> {partnership ? "Partner-controlled data" : "Your scientific know-how"}</span>}
    </div>
  );
}

export default function DiscoveryDeployment({ perspective = "deployment" }) {
  const partnership = perspective === "partnership";
  const stages = partnership ? partnershipStages : deploymentStages;
  const reduced = useReducedMotion();
  const [compact, setCompact] = useState(() => window.matchMedia?.("(max-width: 700px)").matches || false);
  const [stage, setStage] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [visible, setVisible] = useState(false);
  const [pageVisible, setPageVisible] = useState(() => !document.hidden);
  const figure = useRef(null);
  const hasStarted = useRef(false);
  const scene = useRef(null);
  const timeline = useRef({ time: 0, stage: 0, transport: null });
  const still = reduced || compact;
  const active = still ? 3 : stage;
  const running = playing && visible && pageVisible && !still;

  useLayoutEffect(() => {
    scene.current = createDeploymentScene(figure.current);
    scene.current.render(still ? DEPLOYMENT_DURATION : timeline.current.time, Boolean(still));
    scene.current.canvas.style.opacity = "1";
  }, [still]);

  useEffect(() => {
    const query = window.matchMedia?.("(max-width: 700px)");
    const resize = () => setCompact(Boolean(query?.matches));
    query?.addEventListener?.("change", resize);
    return () => query?.removeEventListener?.("change", resize);
  }, []);

  useEffect(() => {
    if (!window.IntersectionObserver) { setVisible(true); return undefined; }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.intersectionRatio >= 0.5) hasStarted.current = true;
      setVisible(hasStarted.current && entry.isIntersecting);
    }, { threshold: [0, 0.5] });
    observer.observe(figure.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onVisibility = () => setPageVisible(!document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  useEffect(() => {
    if (!running) return undefined;
    let frame;
    let previousTime = null;
    const draw = (timestamp) => {
      // Ignore time spent in another tab or offscreen, retaining the exact pose.
      const delta = previousTime === null ? 0 : Math.min(timestamp - previousTime, 64);
      previousTime = timestamp;
      const state = timeline.current;
      const transport = state.transport;
      let finishedSeek = false;
      if (transport) {
        transport.elapsed += delta;
        if (transport.kind === "replay") {
          const fadingOut = transport.elapsed < 320;
          if (!fadingOut) state.time = 0;
          scene.current.canvas.style.opacity = fadingOut
            ? transport.opacity * (1 - smoothProgress(transport.elapsed, 0, 320))
            : smoothProgress(transport.elapsed, 320, 480);
          if (transport.elapsed >= 800) state.transport = null;
        } else {
          const amount = smoothProgress(transport.elapsed, 0, 1100);
          state.time = transport.from + (transport.to - transport.from) * amount;
          scene.current.canvas.style.opacity = transport.opacity + (1 - transport.opacity) * smoothProgress(transport.elapsed, 0, 400);
          if (amount === 1) { state.transport = null; finishedSeek = true; }
        }
      } else {
        state.time = Math.min(DEPLOYMENT_DURATION, state.time + delta);
      }
      scene.current.render(state.time);
      const nextStage = deploymentStage(state.time);
      if (state.stage !== nextStage) { state.stage = nextStage; setStage(nextStage); }
      if (finishedSeek || (!state.transport && state.time >= DEPLOYMENT_DURATION)) {
        setPlaying(false);
        return;
      }
      frame = window.requestAnimationFrame(draw);
    };
    frame = window.requestAnimationFrame(draw);
    return () => window.cancelAnimationFrame(frame);
  }, [running]);

  const restart = () => {
    hasStarted.current = true;
    setVisible(true);
    timeline.current.transport = { kind: "replay", elapsed: 0, opacity: Number(scene.current.canvas.style.opacity) };
    setPlaying(true);
  };
  const selectStage = (index) => {
    hasStarted.current = true;
    setVisible(true);
    timeline.current.transport = { kind: "seek", from: timeline.current.time, to: DEPLOYMENT_STOPS[index], elapsed: 0, opacity: Number(scene.current.canvas.style.opacity) };
    setPlaying(true);
  };
  const togglePlayback = () => {
    hasStarted.current = true;
    setVisible(true);
    if (timeline.current.time >= DEPLOYMENT_DURATION && !timeline.current.transport && !playing) restart();
    else setPlaying(!playing);
  };

  return (
    <figure ref={figure} className="deployment" data-perspective={perspective} data-stage={active} data-playing={running} data-still={Boolean(still)} aria-label={partnership ? "How a scientific partnership would work" : "How Cornucopia connects a research company"}>
      <div className="deployment-toolbar">
        <span>{partnership ? "Inside a research partnership" : "Inside a partner company"}</span>
        {!still && <div className="deployment-playback">
          <button type="button" onClick={togglePlayback} aria-label={playing ? "Pause deployment animation" : "Play deployment animation"}>{playing ? <Pause size={14} /> : <Play size={14} />}</button>
          <button type="button" onClick={restart} aria-label="Replay deployment animation"><RotateCcw size={14} /></button>
        </div>}
      </div>

      <div className="deployment-canvas" role="img" aria-label={partnership ? "Cornucopia and partner scientists would use research history, specialist agents and lab experiments to evaluate a scientific question. Scientists approve experiments. Results feed the next iteration. CCP-001 represents our first-program ambition." : "Cornucopia deploys into a company and connects its proprietary data, Cornucopia data, and lab equipment. Scientists ask agents questions. Scientist-approved experiments feed results into the next iteration, working toward an illustrative research asset, CCP-001."}>
        <div className="deployment-company" aria-hidden="true"><span>{partnership ? "Partner laboratory" : "Your research environment"}</span><LockKeyhole size={13} /></div>
        <svg className="deployment-wires" viewBox="0 0 1000 540" preserveAspectRatio="none" aria-hidden="true">
          <g className="deployment-wire">
            <path data-wire="data-private" pathLength="1" d="M410 152 H230" />
            <path data-wire="data-cornucopia" pathLength="1" d="M590 152 H770" />
            <path data-wire="core-agents" pathLength="1" d="M500 184 V270" />
            <path data-wire="question" pathLength="1" d="M270 319 H393" />
            <path data-wire="lab-trunk" pathLength="1" d="M500 366 V425" />
            <path data-wire="lab-handler" pathLength="1" d="M500 425 H355 V449" />
            <path data-wire="lab-instruments" pathLength="1" d="M500 425 H645 V449" />
          </g>
          <g className="deployment-wire deployment-return"><path data-wire="results" pathLength="1" d="M720 470 H906 Q922 470 922 454 V330 Q922 314 906 314 H614" /></g>
          <g className="deployment-flow deployment-experiment"><path d="M720 470 H906 Q922 470 922 454 V330 Q922 314 906 314 H614" /></g>
          <g className="deployment-wire"><path data-wire="asset" pathLength="1" d="M790 314 V352" /></g>
        </svg>
        <div className="deployment-core" aria-hidden="true">
          <div className="deployment-logo">
            <img className="deployment-logo-light" src="/Logo.svg" alt="" width="152" height="42" />
            <img className="deployment-logo-dark" src="/Logo-dark.svg" alt="" width="152" height="42" />
          </div>
          <span>{partnership ? "AI harness" : "The connective layer"}</span>
        </div>
        <div className="deployment-node-content" aria-hidden="true">
          <DataNode partnership={partnership} />
          <DataNode cornucopia partnership={partnership} />
          <div className="deployment-scientist deployment-reveal deployment-experiment">
            <img src="/images/discovery/principal-scientist.png" alt="" width="44" height="44" />
            <div><span>{partnership ? "Partner scientists" : "Your scientist"}</span><p>“What should we test next?”</p></div>
          </div>
          <div className="deployment-agents deployment-reveal deployment-experiment">
            <div className="deployment-agent-heading"><Bot size={20} strokeWidth={1.3} /><strong>{partnership ? "Specialist agents" : "Your AI team"}</strong></div>
            <div className="deployment-agent-roles"><span>Research</span><span>Plan</span><span>Analyze</span></div>
            <span className="deployment-approval"><Check size={12} /> Scientist approves the run</span>
          </div>
          <div className="deployment-lab">
            <div><Pipette strokeWidth={1.2} /><span>Liquid handlers</span></div>
            <div><Microscope strokeWidth={1.2} /><span>Lab instruments</span></div>
          </div>
          <div className="deployment-feedback deployment-reveal deployment-experiment"><RotateCcw size={13} /><span>Results inform the next experiment</span></div>
          <div className="deployment-asset deployment-reveal deployment-advance"><FlaskConical size={19} /><div><strong>CCP-001</strong><span>{partnership ? "Our first-program ambition" : "Illustrative research asset"}</span></div><ArrowUpRight size={16} /></div>
        </div>
      </div>

      {!still && <div className="deployment-stages" role="group" aria-label="Deployment animation stages">
        <div className="deployment-stage-indicator" style={{ transform: `translateX(${stage * 100}%)` }} aria-hidden="true" />
        {stages.map(({ label }, index) => <button type="button" key={label} aria-pressed={stage === index} onClick={() => selectStage(index)}><span>0{index + 1}</span>{label}</button>)}
        <div className="deployment-progress" aria-hidden="true"><span /></div>
      </div>}
      <figcaption><span className="deployment-captions">{still ? (partnership ? "Working together toward a drug candidate." : "Your company, connected from question to experiment.") : stages.map(({ caption }, index) => <span key={caption} aria-hidden={stage !== index} className={stage === index ? "is-active" : ""}>{caption}</span>)}</span>{!partnership && <span>Illustrative deployment</span>}</figcaption>
    </figure>
  );
}
